var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import DOMPurify from 'dompurify';
import { allowedFeedHref } from './feedLinks';
/**
 * The coach feed's tags: what Knock's markdown renders for the feed
 * templates, and nothing that can navigate, submit or load except a link
 * (Q24): no form, button, input, image, image map, frame, object, base, svg
 * or math. An allowlist, like the attributes: only `href` and `title`.
 */
var COACH_FEED_TAGS = [
    'p', 'br', 'hr', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'span', 'a',
    'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div'
];
var COACH_FEED_CONFIG = {
    ALLOWED_TAGS: COACH_FEED_TAGS,
    ALLOWED_ATTR: ['href', 'title'],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false
};
/**
 * Sanitize Knock's server-rendered feed HTML before it is injected via
 * `dangerouslySetInnerHTML`. The body comes from `feedItemBodyHtml` (Knock's
 * `rendered` output) and can embed values such as `producer_name` that a
 * salesperson sets but a *coach* views — a privilege-crossing surface — so it
 * is sanitized regardless of Knock's own escaping (SCO-406). Kept as a tiny
 * named boundary so the XSS guard is explicit and unit-testable (DOMPurify
 * needs a DOM, so its test runs under the jsdom environment).
 *
 * Links (James 2026-10-19, Q24 (b)): an `<a>` stays only when its href is
 * allowed by {@link allowedFeedHref} (this app's origin, or exactly the Node
 * API origin on a signed-response path), with the href resolved to that
 * absolute URL. Every other link becomes its words, with no href, so a
 * producer named `[Sign in](https://evil.example/login)` shows as "Sign in".
 * The rewrite runs outside DOMPurify, so its serialized result goes through
 * DOMPurify once more: a backstop no test can reach today, since the rewrite
 * adds only text nodes and parsed http(s) hrefs.
 * `origins` has no default: there is no permissive fallback.
 */
export default function sanitizeFeedHtml(html, origins) {
    var fragment = DOMPurify.sanitize(html, __assign(__assign({}, COACH_FEED_CONFIG), { RETURN_DOM_FRAGMENT: true }));
    if (fragment === null) {
        return '';
    }
    // `href` is allowed for the link only: on any other element it would keep
    // an attacker's URL in the DOM, inert but present (fresh review P3-1).
    fragment.querySelectorAll('[href]:not(a)').forEach(function (element) { return element.removeAttribute('href'); });
    fragment.querySelectorAll('a').forEach(function (anchor) {
        var _a, _b;
        var allowed = allowedFeedHref((_a = anchor.getAttribute('href')) !== null && _a !== void 0 ? _a : '', origins);
        if (allowed === null) {
            // A line break inside the link keeps its words apart (Codex on #579).
            anchor.querySelectorAll('br').forEach(function (br) { return br.replaceWith('\n'); });
            anchor.replaceWith(document.createTextNode((_b = anchor.textContent) !== null && _b !== void 0 ? _b : ''));
        }
        else {
            anchor.setAttribute('href', allowed);
        }
    });
    var host = document.createElement('div');
    host.appendChild(fragment);
    return DOMPurify.sanitize(host.innerHTML, COACH_FEED_CONFIG);
}
/**
 * The tags a body's words may sit in. Everything else goes, and DOMPurify
 * drops script, style and template contents with them.
 */
var TEXT_TAGS = [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'span', 'a',
    'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div'
];
/** The block-level tags whose end is a line break in a plain-text body. */
var BLOCK_TAGS = 'p, li, h1, h2, h3, h4, h5, h6, blockquote, pre, div, tr';
/**
 * A producer card's body as PLAIN TEXT, for the banner and the bell (VC-02;
 * James 2026-10-18, Q9). Two sanitizer configurations in a row let something
 * the message carried act inside the page (a link title's markup, then a
 * data-* attribute a component trusts), so producer bodies keep no markup,
 * no attribute and no link: only their words, with block breaks as line
 * breaks. Parsed by DOMPurify into a detached fragment (script, style and
 * template contents dropped) and read as text; never injected as HTML.
 */
export function feedBodyPlainText(html) {
    var _a;
    // The formatting tags only, so a removed element's text survives as words
    // but script, style, template and the like are dropped with their contents.
    var fragment = DOMPurify.sanitize(html, {
        RETURN_DOM_FRAGMENT: true,
        ALLOWED_TAGS: TEXT_TAGS,
        ALLOWED_ATTR: [],
        ALLOW_DATA_ATTR: false,
        ALLOW_ARIA_ATTR: false
    });
    if (fragment === null) {
        return '';
    }
    fragment.querySelectorAll('br').forEach(function (br) { return br.replaceWith('\n'); });
    fragment.querySelectorAll(BLOCK_TAGS).forEach(function (block) { return block.append('\n'); });
    return ((_a = fragment.textContent) !== null && _a !== void 0 ? _a : '')
        .split('\n')
        .map(function (line) { return line.replace(/[ \t]+/g, ' ').trim(); })
        .filter(function (line) { return line !== ''; })
        .join('\n');
}
/**
 * Whether the body carries a link at all (VC-02). Only used to offer the
 * midday's Open; where it goes is never read from the body.
 */
export function feedBodyHasLink(html) {
    var fragment = DOMPurify.sanitize(html, { RETURN_DOM_FRAGMENT: true });
    return (fragment === null || fragment === void 0 ? void 0 : fragment.querySelector('a[href]')) != null;
}
